/**
 * Figma — metrics-calculation-loader (5400:28682) + modal-card (5400:28721).
 */
import { useEffect, useRef, useState } from "react";
import { Box, Button, Dialog, Typography } from "@mui/material";
import TruncationTooltip from "../../components/TruncationTooltip";

import outerRingImg from "../../asserts/gate2/metrics-loader/524a6.png";
import middleRingImg from "../../asserts/gate2/metrics-loader/66517.svg";
import logoFrameImg from "../../asserts/gate2/metrics-loader/bb04c.svg";
import iconClock from "../../asserts/gate2/metrics-loader/af8b1.svg";
import iconHourglass from "../../asserts/gate2/metrics-loader/858ab.svg";
import iconCheck from "../../asserts/gate2/metrics-loader/8900d.svg";

/** @typedef {'pending' | 'running' | 'pass' | 'fail'} MetricRunStatus */

const STATUS_PILL_BASE = {
  position: "absolute",
  right: 16,
  top: "50%",
  transform: "translateY(-50%)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  px: "12px",
  py: "6px",
  borderRadius: "999px",
  border: "1px solid",
  fontFamily: "Lato, sans-serif",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "normal",
  whiteSpace: "nowrap",
};

function MetricStatusPill({ status }) {
  const label =
    status === "running" ? "Calculating" : status === "pass" || status === "fail" ? "Completed" : "Pending";
  const tone =
    status === "running"
      ? { borderColor: "#4A7AC7", color: "#3B6BB5" }
      : status === "pass" || status === "fail"
        ? { borderColor: "#1A8C59", color: "#14704A" }
        : { borderColor: "#999999", color: "#666666" };

  return (
    <TruncationTooltip title={label} placement="top">
      <Box
        sx={{
          ...STATUS_PILL_BASE,
          ...tone,
          maxWidth: "calc(100% - 32px)",
          minWidth: 0,
        }}
      >
        {status === "running" ? (
          <Box
            component="img"
            src={iconHourglass}
            alt=""
            width={16}
            height={16}
            sx={{
              width: 16,
              height: 16,
              flexShrink: 0,
              animation: "gate2MetricsHourglass 6s ease-in-out infinite",
              "@keyframes gate2MetricsHourglass": {
                "0%": { transform: "rotate(0deg)" },
                "25%": { transform: "rotate(180deg)" },
                "50%": { transform: "rotate(360deg)" },
                "75%": { transform: "rotate(540deg)" },
                "100%": { transform: "rotate(720deg)" },
              },
            }}
          />
        ) : status === "pass" || status === "fail" ? (
          <Box component="img" src={iconCheck} alt="" width={14} height={14} sx={{ width: 14, height: 14, flexShrink: 0 }} />
        ) : (
          <Box component="img" src={iconClock} alt="" width={14} height={14} sx={{ width: 14, height: 14, flexShrink: 0 }} />
        )}
        <Box
          component="span"
          data-truncation-measure
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {label}
        </Box>
      </Box>
    </TruncationTooltip>
  );
}

function ArchitecturalLoader({ animate }) {
  return (
    <Box sx={{ position: "relative", width: 140, height: 140, flexShrink: 0 }}>
      <Box
        component="img"
        src={outerRingImg}
        alt=""
        width={130}
        height={130}
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          width: 130,
          height: 130,
          opacity: 0.3,
          transform: "translate(-50%, -50%)",
          ...(animate
            ? {
                animation: "gate2MetricsOuterSpin 24s linear infinite",
                "@keyframes gate2MetricsOuterSpin": {
                  from: { transform: "translate(-50%, -50%) rotate(0deg)" },
                  to: { transform: "translate(-50%, -50%) rotate(360deg)" },
                },
              }
            : {}),
        }}
      />
      <Box
        component="img"
        src={middleRingImg}
        alt=""
        width={100}
        height={100}
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          width: 100,
          height: 100,
          transform: "translate(-50%, -50%)",
          ...(animate
            ? {
                animation: "gate2MetricsMiddleSpin 16s linear infinite reverse",
                "@keyframes gate2MetricsMiddleSpin": {
                  from: { transform: "translate(-50%, -50%) rotate(0deg)" },
                  to: { transform: "translate(-50%, -50%) rotate(360deg)" },
                },
              }
            : {}),
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: 42,
          top: 42,
          width: 56,
          height: 56,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src={logoFrameImg}
          alt=""
          width={52}
          height={42}
          sx={{ width: 52, height: 42 }}
        />
      </Box>
    </Box>
  );
}

function PrototypeBackdrop() {
  return (
    <Box
      aria-hidden
      sx={{
        position: "fixed",
        inset: 0,
        overflow: "hidden",
        bgcolor: "#FFFFFF",
        backgroundImage: `
          linear-gradient(to right, rgba(229, 231, 235, 0.35) 1px, transparent 1px),
          linear-gradient(to bottom, rgba(229, 231, 235, 0.35) 1px, transparent 1px)
        `,
        backgroundSize: "240px 120px",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          p: "40px",
          opacity: 0.35,
          filter: "blur(9px)",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          pointerEvents: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "#005BF0" }} />
            <Box sx={{ width: 100, height: 18, borderRadius: "4px", bgcolor: "#E6E6E6" }} />
          </Box>
          <Box sx={{ display: "flex", gap: "16px" }}>
            <Box sx={{ width: 120, height: 32, borderRadius: "6px", bgcolor: "#E6E6E6" }} />
            <Box sx={{ width: 32, height: 32, borderRadius: "16px", bgcolor: "#8D8D8D" }} />
          </Box>
        </Box>
        <Box sx={{ display: "flex", flex: 1, gap: "24px", minHeight: 0 }}>
          <Box sx={{ width: 240, display: "flex", flexDirection: "column", gap: "16px" }}>
            {[0, 1, 2, 3].map((i) => (
              <Box key={i} sx={{ height: 40, borderRadius: "6px", bgcolor: "#E6E6E6" }} />
            ))}
          </Box>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "24px" }}>
            <Box sx={{ display: "flex", gap: "24px" }}>
              {[0, 1, 2].map((i) => (
                <Box key={i} sx={{ flex: 1, height: 120, borderRadius: "12px", bgcolor: "#E6E6E6" }} />
              ))}
            </Box>
            <Box sx={{ flex: 1, borderRadius: "12px", bgcolor: "#E6E6E6", minHeight: 200 }} />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to right, #FFFFFF 0%, #F9FAFB 100%)",
          opacity: 0.92,
        }}
      />
    </Box>
  );
}

const MODAL_WIDTH = 720;
const MODAL_HEIGHT = 651;

/** Visible metric rows in the loader list; extra rows scroll (Figma consistency). */
const VISIBLE_METRIC_ROWS = 5;
const METRIC_ROW_HEIGHT_PX = 42;
const METRIC_ROW_GAP_PX = 4;
const METRIC_ROW_STEP_PX = METRIC_ROW_HEIGHT_PX + METRIC_ROW_GAP_PX;
const METRIC_LIST_VIEWPORT_HEIGHT_PX =
  VISIBLE_METRIC_ROWS * METRIC_ROW_HEIGHT_PX + (VISIBLE_METRIC_ROWS - 1) * METRIC_ROW_GAP_PX;

function useModalViewportScale(open) {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!open) return undefined;

    const update = () => {
      const maxW = window.innerWidth - 32;
      const maxH = window.innerHeight - 32;
      setScale(Math.min(1, maxW / MODAL_WIDTH, maxH / MODAL_HEIGHT));
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [open]);

  return scale;
}

const CANCEL_BTN_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 13,
  fontWeight: 700,
  lineHeight: "24px",
  color: "#555555",
  textTransform: "none",
  boxSizing: "border-box",
  bgcolor: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  backgroundImage: "none",
  border: "1px solid #BBBBBB",
  borderRadius: "4px",
  px: "16px",
  py: "4px",
  minHeight: 32,
  boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.08)",
  "& .MuiTouchRipple-root": { display: "none" },
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#FFFFFF",
    backgroundColor: "#FFFFFF",
    backgroundImage: "none",
    border: "1px solid #BBBBBB",
    color: "#555555",
    boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.08)",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    color: "#555555",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #555555",
    boxShadow: "0px 1px 1px rgba(0, 0, 0, 0.08)",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #555555",
    color: "#555555",
  },
  "&:active:not(.Mui-disabled)": {
    color: "#555555",
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage: "none !important",
    border: "1px solid #BBBBBB",
    boxShadow: "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)",
  },
  "&.Mui-disabled": {
    color: "#BBBBBB !important",
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage: "none !important",
    border: "1px solid #E6E6E6 !important",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    opacity: "1 !important",
    cursor: "not-allowed",
  },
};

export default function Gate2ExecutingMetricsDialog({ open, metrics = [], onCancel }) {
  const total = metrics.length;
  const settled = metrics.filter((m) => m.status === "pass" || m.status === "fail").length;
  const progressPct = total ? (settled / total) * 100 : 0;
  const viewportScale = useModalViewportScale(open);
  const metricListRef = useRef(null);
  const scrollList = total > VISIBLE_METRIC_ROWS;

  useEffect(() => {
    if (!open || !scrollList || !metricListRef.current) return;

    const runningIdx = metrics.findIndex((m) => m.status === "running");
    const activeIdx =
      runningIdx >= 0
        ? runningIdx
        : metrics.findIndex((m) => m.status === "pending");

    const listEl = metricListRef.current;
    if (activeIdx < 0) return;

    if (activeIdx < VISIBLE_METRIC_ROWS) {
      listEl.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const scrollTop = (activeIdx - VISIBLE_METRIC_ROWS + 1) * METRIC_ROW_STEP_PX;
    listEl.scrollTo({ top: scrollTop, behavior: "smooth" });
  }, [open, scrollList, metrics]);

  return (
    <Dialog
      open={open}
      fullScreen
      aria-labelledby="gate2-executing-metrics-dialog-title"
      PaperProps={{
        sx: {
          m: 0,
          maxWidth: "none",
          width: "100%",
          height: "100%",
          bgcolor: "transparent",
          boxShadow: "none",
          overflow: "hidden",
          alignItems: "center",
          justifyContent: "center",
          display: "flex",
        },
      }}
      slotProps={{
        backdrop: { sx: { bgcolor: "transparent" } },
      }}
    >
      <PrototypeBackdrop />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          transform: `scale(${viewportScale})`,
          transformOrigin: "center center",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: MODAL_WIDTH,
            height: "auto",
            maxHeight: MODAL_HEIGHT,
            boxSizing: "border-box",
            bgcolor: "#FFFFFF",
            borderRadius: "16px",
            px: "32px",
            pt: "24px",
            pb: "20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "16px",
            overflow: "hidden",
          }}
        >
        <Box
          sx={{
            pointerEvents: "none",
            position: "absolute",
            inset: 0,
            borderRadius: "20px",
            border: "3px solid #1756E5",
          }}
        />

        <ArchitecturalLoader animate />

        <Typography
          id="gate2-executing-metrics-dialog-title"
          component="h2"
          sx={{
            fontFamily: "Lato, sans-serif",
            fontSize: 30,
            fontWeight: 900,
            lineHeight: "38px",
            color: "#383838",
            textAlign: "center",
            width: "100%",
            flexShrink: 0,
          }}
        >
          Executing &amp; Calculating Metrics
        </Typography>

        <Box
          ref={metricListRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: `${METRIC_ROW_GAP_PX}px`,
            py: "8px",
            width: "100%",
            flex: scrollList ? `0 0 ${METRIC_LIST_VIEWPORT_HEIGHT_PX + 16}px` : "0 0 auto",
            minHeight: scrollList ? METRIC_LIST_VIEWPORT_HEIGHT_PX + 16 : "auto",
            maxHeight: scrollList ? METRIC_LIST_VIEWPORT_HEIGHT_PX + 16 : "none",
            overflowY: scrollList ? "auto" : "visible",
            overflowX: "hidden",
            scrollBehavior: "smooth",
          }}
        >
          {metrics.map((row) => {
            return (
              <Box
                key={row.key}
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  height: METRIC_ROW_HEIGHT_PX,
                  minHeight: METRIC_ROW_HEIGHT_PX,
                  flexShrink: 0,
                  p: "16px",
                  boxSizing: "border-box",
                  width: "100%",
                }}
              >
                <TruncationTooltip title={row.name}>
                  <Typography
                    data-truncation-measure
                    sx={{
                      fontFamily: "Lato, sans-serif",
                      fontSize: 16,
                      fontWeight: 700,
                      lineHeight: "36px",
                      color: "#383838",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      pr: "140px",
                      minWidth: 0,
                      display: "block",
                    }}
                  >
                    {row.name}
                  </Typography>
                </TruncationTooltip>
                <MetricStatusPill status={row.status} />
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%", flexShrink: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontSize: 12,
                fontWeight: 400,
                lineHeight: "18px",
                color: "#555555",
              }}
            >
              Overall Progress
            </Typography>
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontSize: 12,
                fontWeight: 700,
                lineHeight: "18px",
                color: "#005BF0",
                textAlign: "right",
              }}
            >
              {`${settled} of ${total} completed`}
            </Typography>
          </Box>
          <Box
            sx={{
              width: "100%",
              height: 10,
              borderRadius: "999px",
              bgcolor: "#E6E6E6",
              overflow: "hidden",
              display: "flex",
              alignItems: "stretch",
            }}
          >
            <Box
              sx={{
                height: 10,
                borderRadius: "999px",
                bgcolor: "#005BF0",
                width: settled === 0 ? 2 : `${progressPct}%`,
                transition: "width 0.35s ease",
              }}
            />
          </Box>
        </Box>

        <Typography
          sx={{
            fontFamily: "Lato, sans-serif",
            fontSize: 12,
            fontWeight: 700,
            lineHeight: "18px",
            color: "#707070",
            textAlign: "center",
            width: "100%",
            flexShrink: 0,
          }}
        >
          {`Evaluating ${total} metric${total === 1 ? "" : "s"} across all test suites. This may take up to 2 minutes.`}
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "center", flexShrink: 0, pb: "8px" }}>
          <Button variant="outlined" disableElevation onClick={onCancel} sx={CANCEL_BTN_SX}>
            Cancel Evaluation
          </Button>
        </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
